import type {
  ContentCourse,
  ContentCourseCreateInput,
  ContentCourseUpdateInput,
  ContentCourseWithLessons,
  ContentLessonCreateInput,
  ContentLessonUpdateInput,
  ContentLessonWithMedia,
  ContentMedia,
  ContentCourseReadiness,
  FounderCategoryCode,
} from '@doneverse/shared'
import { api, readNotReadyPayload } from '../api/index.js'

/**
 * Admin authoring API for the Masterclass Vault — the CMS surface for
 * `content_courses → content_lessons → content_media`.
 *
 * Assets and change history are NOT here: those endpoints are shared with the
 * playbook editor and live in `cmsAssetsService`.
 */

/**
 * Course fields as the editor sees them.
 *
 * `card_image_r2_key` is the raw key the form round-trips on save;
 * `card_image_preview_url` is a short-lived signature for display, falling back
 * to the imported `card_image_url` for a LearnWorlds course that has no key.
 * Never write the preview back — it expires.
 */
export interface AdminMasterclassCourseAssets {
  card_image_r2_key: string | null
  card_image_preview_url: string | null
}

export interface AdminMasterclassCourseSummary extends ContentCourse, AdminMasterclassCourseAssets {
  lesson_count: number
}

// The admin endpoints enrich each media row with the founder categories it
// targets (resolved server-side from the row's audience). Everyone-visible
// variants come back with an empty array.
export interface AdminMasterclassMedia extends ContentMedia {
  founder_categories: FounderCategoryCode[]
  /** Raw keys — round-tripped on save, never replaced by a signed URL. */
  image_r2_key: string | null
  thumbnail_r2_key: string | null
  /** Display-only signatures for the keys above. */
  image_preview_url: string | null
  thumbnail_preview_url: string | null
  /** Signed playback URL for self-hosted (`provider === 'r2'`) video. */
  video_preview_url: string | null
}

export interface AdminMasterclassLesson extends Omit<ContentLessonWithMedia, 'media'> {
  media: AdminMasterclassMedia[]
}

export type AdminMasterclassCourseDetail = Omit<ContentCourseWithLessons, 'lessons'> &
  AdminMasterclassCourseAssets & {
    lessons: AdminMasterclassLesson[]
    lesson_count: number
  }

interface ListResponse {
  items: AdminMasterclassCourseSummary[]
}

/** Thrown when the API refuses a publish; carries what is still missing. */
export class CourseNotReadyError extends Error {
  constructor(readonly readiness: Pick<ContentCourseReadiness, 'blocking' | 'warnings'>) {
    super('Course is not ready to publish')
    this.name = 'CourseNotReadyError'
  }
}

export const adminMasterclassVaultService = {
  // Courses
  listCourses: async (slug?: string): Promise<AdminMasterclassCourseSummary[]> => {
    const qs = slug ? `?slug=${encodeURIComponent(slug)}` : ''
    const res = await api.get(`/v1/admin/masterclass-vault/courses${qs}`).json<ListResponse>()
    return res.items
  },
  getCourse: async (id: string): Promise<AdminMasterclassCourseDetail> => {
    return api
      .get(`/v1/admin/masterclass-vault/courses/${encodeURIComponent(id)}`)
      .json<AdminMasterclassCourseDetail>()
  },
  createCourse: async (input: ContentCourseCreateInput): Promise<AdminMasterclassCourseDetail> => {
    return api.post(input, '/v1/admin/masterclass-vault/courses').json<AdminMasterclassCourseDetail>()
  },
  updateCourse: async (
    id: string,
    patch: ContentCourseUpdateInput,
  ): Promise<AdminMasterclassCourseDetail> => {
    try {
      return await api
        .patch(patch, `/v1/admin/masterclass-vault/courses/${encodeURIComponent(id)}`)
        .json<AdminMasterclassCourseDetail>()
    } catch (error) {
      const readiness = readNotReadyPayload(error)
      if (readiness) throw new CourseNotReadyError(readiness)
      throw error
    }
  },
  deleteCourse: async (id: string): Promise<void> => {
    await api.delete(`/v1/admin/masterclass-vault/courses/${encodeURIComponent(id)}`).res()
  },
  /** What is still missing before this course can be published. */
  readiness: async (id: string): Promise<ContentCourseReadiness> => {
    return api
      .get(`/v1/admin/masterclass-vault/courses/${encodeURIComponent(id)}/readiness`)
      .json<ContentCourseReadiness>()
  },
  // Persist a drag-reorder: send the full ordered id list; the server rewrites
  // each course's position to its index.
  reorderCourses: async (ids: string[], slug?: string): Promise<void> => {
    const qs = slug ? `?slug=${encodeURIComponent(slug)}` : ''
    await api.post({ ids }, `/v1/admin/masterclass-vault/courses/reorder${qs}`).res()
  },

  // Lessons
  createLesson: async (
    courseId: string,
    input: ContentLessonCreateInput,
  ): Promise<AdminMasterclassLesson> => {
    return api
      .post(input, `/v1/admin/masterclass-vault/courses/${encodeURIComponent(courseId)}/lessons`)
      .json<AdminMasterclassLesson>()
  },
  updateLesson: async (id: string, patch: ContentLessonUpdateInput): Promise<AdminMasterclassLesson> => {
    return api
      .patch(patch, `/v1/admin/masterclass-vault/lessons/${encodeURIComponent(id)}`)
      .json<AdminMasterclassLesson>()
  },
  deleteLesson: async (id: string): Promise<void> => {
    await api.delete(`/v1/admin/masterclass-vault/lessons/${encodeURIComponent(id)}`).res()
  },
  /** Same contract as the course reorder: the FULL ordered lesson id list. */
  reorderLessons: async (courseId: string, ids: string[]): Promise<void> => {
    await api
      .post({ ids }, `/v1/admin/masterclass-vault/courses/${encodeURIComponent(courseId)}/lessons/reorder`)
      .res()
  },
}
